
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { FlatTransport } from '@/models/FlatTransport'
import { Transport } from '@/models/Transport'
import Notification from '@/classes/Notification'
import i18n from '@/i18n'
import axios from 'axios'
import InvoiceType from '@/enums/InvoiceType'
import { InvoiceLine } from '@/models/accounting/InvoiceLine'
import FinancialType from '@/enums/FinancialType'
import { PluralResponse } from 'coloquent/dist/response/PluralResponse'
import { Customer } from '@/models/accounting/Customer'

@Component({
	components: {},
	computed: {
		InvoiceType() {
			return InvoiceType
		}
	}
})
export default class PreparePurchaseOrderDialog extends Vue {
	@Prop({ required: false, default: () => [] })
	transports!: Array<FlatTransport> | Array<Transport>

	@Prop({ required: false, default: false })
	opened!: boolean

	isLoading: boolean = true
	isExporting: boolean = false
	isMenuOpen = false

	clientOrderReference: string = ''
	revenueOrCost: InvoiceType.SALES | InvoiceType.PURCHASE = InvoiceType.SALES
	financialRegistrations: Array<InvoiceLine> | null = null

	customer: Customer | null = null
	customers: Array<Customer> = []

	async created() {
		await this.fetchAllFinancialRegistrations()

		this.customers =
			this.financialRegistrations
				?.map((line) => line.getCustomer())
				.filter((customer) => customer !== undefined) ?? []
		const allCustomer = new Customer()
		allCustomer.name = this.$t('preparePurchaseOrder.companyType.otherOptions') as string
		allCustomer.relationTypes = ['allCustomers']
		allCustomer.setApiId('all')
		this.customers.push(allCustomer)

		this.isLoading = false
	}

	get filteredTransports() {
		if (!this.financialRegistrations?.length || !this.customer || this.customer.getApiId() === 'all')
			return this.transports

		const linesWithNewCustomerRelation = this.financialRegistrations?.filter((line) => {
			return line.getCustomer()?.getApiId() === this.customer?.getApiId()
		})

		const transportIds = linesWithNewCustomerRelation?.map((line) => {
			return line.getTransport()?.getApiId()
		})

		return (this.transports as Array<FlatTransport>).filter(
			(transport) => transportIds?.includes(transport.getApiId())
		)
	}

	get transportsWithMatchingFinancialRegistrations() {
		if (!this.customer || this.customer.getApiId() === 'all') return this.filteredTransports

		const transportsHasInvoiceLines = this.filteredInvoiceLines.map((line) => {
			return line.getTransport()?.getApiId()
		})

		return (this.filteredTransports as Array<FlatTransport>).filter(
			(t) => transportsHasInvoiceLines?.includes(t.getApiId())
		)
	}

	get filteredInvoiceLines() {
		return this.filteredTransports.map((transport) => this.linesPerTransport(transport)).flat()
	}

	get hasSameClient() {
		return (this.filteredTransports as Array<FlatTransport>).reduce(
			(acc, val, idx) => acc && (idx === 0 || this.filteredTransports[idx - 1].clientId === val.clientId),
			true
		)
	}

	closeDialog() {
		this.$emit('closed')
	}

	async exportData(withDownload = true) {
		try {
			this.isExporting = true

			const fauxTransport = new Transport()
			const requestBody = {
				invoiceType: this.revenueOrCost,
				withDownload,
				...(!this.customer || this.customer?.getApiId() === 'all'
					? {
							transports: this.transports.map((t) => ({ id: t.getApiId() })),
							clientOrderReference: this.clientOrderReference || null // '' should be converted to null
					  }
					: {
							financialRegistrations: this.filteredInvoiceLines.map((line) => ({ id: line.getApiId() })),
							clientOrderReference: this.clientOrderReference || null, // '' should be converted to null
							customer: this.customer.getApiId()
					  })
			}

			if (withDownload) {
				const response = await axios.post(
					`${fauxTransport.getJsonApiBaseUrl()}${fauxTransport.getJsonApiType()}/exportDeliveries`,
					requestBody,
					{
						responseType: 'blob',
						headers: {
							'Accept-Language': i18n.locale
						}
					}
				)

				await Notification.Success(i18n.t('preparePurchaseOrder.submit.success'))
				window.open(URL.createObjectURL(response.data), '_blank')
			} else {
				await axios.post(
					`${fauxTransport.getJsonApiBaseUrl()}${fauxTransport.getJsonApiType()}/exportDeliveries`,
					requestBody,
					{
						responseType: 'blob',
						headers: {
							'Accept-Language': i18n.locale
						}
					}
				)

				await Notification.Success(i18n.t('preparePurchaseOrder.submit.success_without_download'))
			}
			this.closeDialog()
		} catch (e) {
			// eslint-disable-next-line
			console.error(e)
			await Notification.Error(i18n.t('preparePurchaseOrder.submit.error'))
		} finally {
			this.isExporting = false
		}
	}

	async fetchAllFinancialRegistrations() {
		const transportIds = this.transports?.map((t) => t.getApiId())
		let query = InvoiceLine.query()

		transportIds?.forEach((id) => (query = query.option('filter[transports][]', id as string)))
		query = query.option('filter[financialType]', FinancialType.FINANCIAL_REGISTRATION)
		this.financialRegistrations = ((await query.get()) as PluralResponse).getData() as Array<InvoiceLine>
	}

	formatRelationTypes(types: Array<string>) {
		return types?.map((type) => this.$t(`preparePurchaseOrder.companyType.${type}`)).join(', ')
	}

	linesPerTransport(transport: Transport | FlatTransport) {
		const allLinesPerTransport =
			this.financialRegistrations?.filter((line) => line.getTransport()?.getApiId() === transport.getApiId()) ??
			[]
		let filteredLinesPerTransport: Array<InvoiceLine> = []

		if (this.revenueOrCost === InvoiceType.PURCHASE) {
			filteredLinesPerTransport = allLinesPerTransport.filter((line) => line.invoiceType === InvoiceType.PURCHASE)
		} else {
			filteredLinesPerTransport = allLinesPerTransport.filter((line) => line.invoiceType === InvoiceType.SALES)
		}

		if (this.customer && this.customer.getApiId() !== 'all') {
			filteredLinesPerTransport = filteredLinesPerTransport.filter(
				(line) => line.getCustomer()?.getApiId() === this.customer?.getApiId()
			)
		}

		return filteredLinesPerTransport
	}
}
